import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CallGuideDialog from "components/consultation/call/CallGuideDialog";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import useConsultationContext from "hook/useConsultationContext";

import { MEDIA_MODE } from "hook/useDevices";

import VideoCall from "../appointmentByAdmin/VideoConsultationComponents/VideoCall";
import momentTz from "moment-timezone";

import {
  CONSULTATION_STAGE,
  resolvePatientConsultationStage,
} from "./utils/resolvePatientConsultationStage";
import ConsultationSidePanel from "components/consultation/call/ConsultationSidePanel";
import ConsultationDocuments from "components/consultation/call/ConsultationDocuments";
import axios from "axios";
import { authHeader } from "auth/authHeader";
import PatientPostConsultation from "components/consultation/post/PatientPostConsultation";

/*
 * =========================================================
 * SINGLE-USE WAITING ROOM ENTRY
 * =========================================================
 *
 * This is frontend flow control only.
 *
 * Backend consultation context remains
 * the actual timing/access authority.
 */
const resolveConsultationEntry = (routeState) => {
  const validMediaMode =
    routeState?.mediaMode === MEDIA_MODE.AUDIO_VIDEO ||
    routeState?.mediaMode === MEDIA_MODE.AUDIO_ONLY;

  if (
    routeState?.enteredFromWaitingRoom !== true ||
    routeState?.privacyAccepted !== true ||
    !validMediaMode
  ) {
    return null;
  }

  return {
    enteredFromWaitingRoom: true,

    privacyAccepted: true,

    mediaMode: routeState.mediaMode,

    providerName:
      typeof routeState?.providerName === "string"
        ? routeState.providerName
        : "Health professional",
  };
};

const PatientConsultationRoom = () => {
  const navigate = useNavigate();

  const location = useLocation();

  const params = useParams();

  const appointmentId = params.appointmentId || params.id || null;

  /*
   * =========================================================
   * ENTRY STATE
   * =========================================================
   *
   * Capture once.
   *
   * We later remove router state so F5,
   * direct URL access or a new tab must
   * return through the Waiting Room.
   */
  const [consultationEntry] = useState(() =>
    resolveConsultationEntry(location.state),
  );

  const mediaMode = consultationEntry?.mediaMode || null;

  const startWithVideo = mediaMode === MEDIA_MODE.AUDIO_VIDEO;

  /*
   * Consume the navigation state.
   *
   * The current mounted component keeps
   * consultationEntry in React state.
   *
   * A refresh does not.
   */
  useEffect(() => {
    if (!consultationEntry) {
      return;
    }

    navigate(location.pathname, {
      replace: true,
      state: null,
    });
  }, [consultationEntry, location.pathname, navigate]);

  /*
   * Refresh / direct /call access
   * returns to Waiting Room.
   */
  useEffect(() => {
    if (!appointmentId || consultationEntry) {
      return;
    }

    navigate(
      `/appointment-consultation/${encodeURIComponent(appointmentId)}/waiting`,
      {
        replace: true,
      },
    );
  }, [appointmentId, consultationEntry, navigate]);

  /*
   * =========================================================
   * CONSULTATION CONTEXT
   * =========================================================
   */

  const { consultationContext, loading, error, refresh } =
    useConsultationContext({
      appointmentId,

      enabled: Boolean(appointmentId && consultationEntry),
    });

  const consultationStage = useMemo(
    () => resolvePatientConsultationStage(consultationContext),
    [consultationContext],
  );

  const timing = consultationContext?.timing;

  const consultationAvailable =
    consultationContext?.consultationAvailable === true;

  const joinable = timing?.joinable === true;

  const hardEnded =
    timing?.hardEnded === true ||
    consultationStage === CONSULTATION_STAGE.HARD_ENDED;

  /*
   * The consultation endpoint is now
   * the source for call-room appointment
   * information.
   */
  const appointmentData = consultationContext?.appointment || {
    appointmentId,
  };

  const providerName = consultationEntry?.providerName || "Health professional";

  const displayTimezone =
    localStorage.getItem("Time-Zone") || momentTz.tz.guess() || "UTC";

  const appointmentTimeLabel = timing?.slotAt
    ? momentTz.utc(timing.slotAt).tz(displayTimezone).format("hh:mm A")
    : "Unavailable";

  /*
   * Keep the current Agora configuration
   * for this checkpoint.
   *
   * Secure backend-generated RTC token
   * comes in a later API checkpoint.
   */
  const agoraAppId = process.env.REACT_APP_AGORA_APP_ID;

  /*
   * =========================================================
   * POST-CALL / TEMPORARY LEAVE STATE
   * =========================================================
   */

  const [callEnded, setCallEnded] = useState(false);

  const [callEndReason, setCallEndReason] = useState(null);

  const [rejoinChecking, setRejoinChecking] = useState(false);

  const [postCallError, setPostCallError] = useState("");

  const [sidePanelOpen, setSidePanelOpen] = useState(false);

  const [sidePanelTab, setSidePanelTab] = useState("chat");

  const [documents, setDocuments] = useState([]);
  const [appointmentComments, setAppointmentComments] = useState([]);

  const [documentsLoading, setDocumentsLoading] = useState(false);

  const [documentsError, setDocumentsError] = useState("");

  const [callGuideOpen, setCallGuideOpen] = useState(false);

  /*
   * =========================================================
   * DOCUMENT_LOADER
   * =========================================================
   */

  const loadAppointmentDetails = async () => {
    if (!appointmentId || documentsLoading) {
      return;
    }

    setDocumentsLoading(true);
    setDocumentsError("");

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      if (!backendUrl) {
        throw new Error("Backend configuration is unavailable.");
      }

      const baseUrl = backendUrl.replace(/\/+$/, "");

      const response = await axios.get(
        `${baseUrl}/appointment/${encodeURIComponent(appointmentId)}`,
        {
          headers: authHeader(),
        },
      );

      const payload = response?.data?.data;

      const loadedAppointment = Array.isArray(payload) ? payload[0] : payload;

      setDocuments(
        Array.isArray(loadedAppointment?.attachments)
          ? loadedAppointment.attachments
          : [],
      );

      setAppointmentComments(
        Array.isArray(loadedAppointment?.AppointmentComments)
          ? loadedAppointment.AppointmentComments
          : Array.isArray(loadedAppointment?.AppointmentComment)
            ? loadedAppointment.AppointmentComment
            : [],
      );
    } catch (documentsRequestError) {
      setDocumentsError(
        documentsRequestError?.response?.data?.message ||
          documentsRequestError?.message ||
          "Unable to load reports and documents.",
      );
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleOpenChat = () => {
    setSidePanelTab("chat");
    setSidePanelOpen(true);
  };

  const handleOpenDocuments = () => {
    setSidePanelTab("documents");

    setSidePanelOpen(true);

    /*
     * Fetch fresh Appointment Details
     * data when Reports is requested.
     */
    void loadAppointmentDetails();
  };

  const handleSidePanelTabChange = (nextTab) => {
    setSidePanelTab(nextTab);

    if (nextTab === "documents") {
      void loadAppointmentDetails();
    }
  };

  /*
   * =========================================================
   * NAVIGATION
   * =========================================================
   */

  const handleBackToWaitingRoom = () => {
    if (!appointmentId) {
      navigate("/appointments");

      return;
    }

    navigate(
      `/appointment-consultation/${encodeURIComponent(appointmentId)}/waiting`,
      {
        replace: true,
      },
    );
  };

  const handleLeaveConsultation = () => {
    if (!appointmentId) {
      navigate("/appointments", {
        replace: true,
      });

      return;
    }

    navigate(`/appointment-consultation/${encodeURIComponent(appointmentId)}`, {
      replace: true,
    });
  };

  /*
   * =========================================================
   * VIDEOCALL END CALLBACK
   * =========================================================
   *
   * VideoCall has already disconnected
   * Agora before this callback runs.
   */
  const handleCallEnd = ({ reason } = {}) => {
    setCallEndReason(reason || "left");

    setCallEnded(true);

    /*
     * Refresh documents after the
     * actual call closes so a provider
     * report uploaded during the call
     * can appear here.
     */
    void loadAppointmentDetails();
  };

  /*
   * =========================================================
   * EXPLICIT REJOIN
   * =========================================================
   *
   * No automatic rejoin.
   *
   * User explicitly chooses:
   * Go Back to Call
   *
   * Backend is revalidated first.
   */
  const handleReturnToCall = async () => {
    if (rejoinChecking) {
      return;
    }

    setPostCallError("");

    setRejoinChecking(true);

    try {
      const freshContext = await refresh({
        silent: true,
      });

      const freshTiming = freshContext?.timing;

      const canRejoin =
        freshContext?.consultationAvailable === true &&
        freshTiming?.joinable === true &&
        freshTiming?.hardEnded !== true;

      if (!canRejoin) {
        setPostCallError(
          freshTiming?.hardEnded
            ? "The consultation time has ended."
            : "The consultation can no longer be rejoined.",
        );

        return;
      }

      setCallEndReason(null);

      /*
       * VideoCall is mounted again.
       *
       * Its existing mount behavior
       * starts a fresh Agora connection.
       *
       * The original selected mediaMode
       * remains unchanged during this
       * mounted page session.
       */
      setCallEnded(false);
    } catch (rejoinError) {
      setPostCallError(
        rejoinError?.response?.data?.message ||
          rejoinError?.message ||
          "Unable to rejoin the consultation.",
      );
    } finally {
      setRejoinChecking(false);
    }
  };

  /*
   * =========================================================
   * MISSING APPOINTMENT
   * =========================================================
   */

  if (!appointmentId) {
    return (
      <Box sx={{ p: 2 }}>
        <Paper
          sx={{
            maxWidth: 700,
            mx: "auto",
            p: 3,
            borderRadius: 3,
          }}
        >
          <Stack spacing={2}>
            <Alert severity="error">Appointment ID is unavailable.</Alert>

            <Button
              variant="outlined"
              onClick={() => navigate("/appointments")}
            >
              Back to Appointments
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  /*
   * =========================================================
   * INVALID / REFRESHED ENTRY
   * =========================================================
   *
   * Redirect effect is already sending
   * the patient to Waiting Room.
   */
  if (!consultationEntry) {
    return (
      <Box
        sx={{
          minHeight: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />

          <Typography variant="body2" color="text.secondary">
            Returning to the Waiting Room...
          </Typography>
        </Stack>
      </Box>
    );
  }

  /*
   * =========================================================
   * LOADING
   * =========================================================
   */

  if (loading && !consultationContext) {
    return (
      <Box
        sx={{
          minHeight: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />

          <Typography variant="body2" color="text.secondary">
            Verifying consultation...
          </Typography>
        </Stack>
      </Box>
    );
  }

  /*
   * =========================================================
   * API ERROR
   * =========================================================
   */

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Paper
          sx={{
            maxWidth: 700,
            mx: "auto",
            p: 3,
            borderRadius: 3,
          }}
        >
          <Stack spacing={2}>
            <Alert severity="error">
              Unable to verify consultation access.
            </Alert>

            <Button
              variant="contained"
              onClick={() => {
                refresh().catch(() => {});
              }}
            >
              Try Again
            </Button>

            <Button
              variant="outlined"
              startIcon={<ArrowBackOutlinedIcon />}
              onClick={handleBackToWaitingRoom}
            >
              Back to Waiting Room
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  /*
   * =========================================================
   * HARD END
   * =========================================================
   */

  if (hardEnded) {
    return (
      <Box sx={{ p: 2 }}>
        <Paper
          sx={{
            maxWidth: 700,
            mx: "auto",
            p: 3,
            borderRadius: 3,
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h5">Consultation Ended</Typography>

            <Alert severity="info">
              The consultation access period has ended.
            </Alert>

            <Button variant="contained" onClick={handleLeaveConsultation}>
              Back to Appointment Details
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  /*
   * =========================================================
   * NOT JOINABLE
   * =========================================================
   */

  if (!consultationAvailable || !joinable) {
    return (
      <Box sx={{ p: 2 }}>
        <Paper
          sx={{
            maxWidth: 700,
            mx: "auto",
            p: 3,
            borderRadius: 3,
          }}
        >
          <Stack spacing={2}>
            <Alert severity="warning">
              This consultation is not currently available to join.
            </Alert>

            <Button
              variant="outlined"
              startIcon={<ArrowBackOutlinedIcon />}
              onClick={handleBackToWaitingRoom}
            >
              Back to Waiting Room
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  /*
   * =========================================================
   * AGORA CONFIGURATION
   * =========================================================
   */

  if (!agoraAppId) {
    return (
      <Box sx={{ p: 2 }}>
        <Paper
          sx={{
            maxWidth: 700,
            mx: "auto",
            p: 3,
            borderRadius: 3,
          }}
        >
          <Stack spacing={2}>
            <Alert severity="error">
              Video consultation configuration is unavailable.
            </Alert>

            <Button variant="outlined" onClick={handleBackToWaitingRoom}>
              Back to Waiting Room
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  /*
   * =========================================================
   * POST-CALL CHECKPOINT
   * =========================================================
   *
   * This is intentionally simple for now.
   *
   * Later this becomes the real patient
   * post-consultation panel with review.
   */
  if (callEnded) {
    return (
      <PatientPostConsultation
        canReturnToCall={!hardEnded}
        rejoinChecking={rejoinChecking}
        postCallError={postCallError}
        documents={documents}
        documentsLoading={documentsLoading}
        documentsError={documentsError}
        appointmentId={appointmentId}
        onReturnToCall={handleReturnToCall}
        onLeaveConsultation={handleLeaveConsultation}
        onRefreshDocuments={loadAppointmentDetails}
      />
    );
  }

  /*
   * =========================================================
   * ACTIVE CONSULTATION
   * =========================================================
   */

  return (
    /*
     * =========================================================
     * Call Guid Dialog
     * =========================================================
     */
    <>
      <CallGuideDialog
        open={callGuideOpen}
        onClose={() => setCallGuideOpen(false)}
      />

      <Box
        sx={{
          width: "100%",
          boxSizing: "border-box",
          p: {
            xs: 1,
            sm: 2,
          },
        }}
      >
        <Stack
          spacing={1.5}
          sx={{
            width: "100%",
            maxWidth: 1440,
            mx: "auto",
          }}
        >
          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            spacing={{
              xs: 1.5,
              md: 1,
            }}
            alignItems={{
              xs: "stretch",
              md: "center",
            }}
            justifyContent={{
              xs: "flex-start",
              md: "flex-end",
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Consultation mode:{" "}
              </Typography>

              <Typography variant="caption" fontWeight={500}>
                {mediaMode === MEDIA_MODE.AUDIO_ONLY
                  ? "Audio Only"
                  : "Audio & Video"}
              </Typography>
            </Box>

            <Typography
              variant="caption"
              color="#db6c6c"
              sx={{
                flex: 1,
                textAlign: {
                  xs: "left",
                  md: "center",
                },
              }}
            >
              PRIVACY NOTICE: This media preview may contain personal data or
              voices. Unauthorized recording, redistribution, or screenshotting
              of this content is prohibited.
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              sx={{
                flexShrink: 0,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<ChatOutlinedIcon />}
                onClick={handleOpenChat}
              >
                Chat
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<DescriptionOutlinedIcon />}
                onClick={handleOpenDocuments}
              >
                Reports
              </Button>

              <Button
                size="small"
                // startIcon={}
                onClick={() => setCallGuideOpen(true)}
              >
                <InfoOutlinedIcon />
              </Button>
            </Stack>
          </Stack>

          <VideoCall
            appId={agoraAppId}
            channelName={appointmentId}
            token={null}
            onCallEnd={handleCallEnd}
            appointmentData={appointmentData}
            providerName={providerName}
            appointmentTimeLabel={appointmentTimeLabel}
            slotAt={timing?.slotAt}
            serverNow={consultationContext?.serverNow}
            hardEndsAt={timing?.hardEndsAt}
            startWithVideo={startWithVideo}
          />

          {/* // REPORT VIEW */}

          <ConsultationSidePanel
            open={sidePanelOpen}
            activeTab={sidePanelTab}
            onTabChange={handleSidePanelTabChange}
            onClose={() => setSidePanelOpen(false)}
            appointmentId={appointmentId}
            attachments={documents}
            comments={appointmentComments}
            documentsLoading={documentsLoading}
            documentsError={documentsError}
            onRefreshDocuments={() => loadAppointmentDetails()}
          />
        </Stack>
      </Box>
    </>
  );
};

export default PatientConsultationRoom;
